import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle, faCircle, faList, faSave, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import {
  fetchSystemCaregiverById,
  updateSystemCaregiver,
  createSystemCaregiver,
  invalidateSystemCaregiverListCache
} from '../../../store/slices/systemCaregiverSlice';
import { useBreadcrumb } from '../../../contexts/BreadcrumbContext';
import { toast } from 'react-toastify';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../utils/phoneUtils';
import PhoneNumberInput from '../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../hooks/useUsernameEmailAvailability';
import practiceService from '../../../services/practiceService';
import { isEvitalsMonitoringPractice } from '../../../utils/practiceMonitoringUtils';
import { matchesWordStart } from '../../../utils/searchMatch';
import './AddSystemCaregiver.css';
import '../../PracticeManagement/PatientManagement/AddPatient/AddPatientModal.css';
import { validatePassword, applyPasswordServerFieldError, extractSubmissionErrorMessage } from '../../../utils/passwordValidation';
import AddFormModel from '../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../components/SkeletonLoader/FormDetailSkeleton';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../utils/constants';

/** Matches validatePassword() — only these count as special characters. */
const PASSWORD_SPECIAL_CHAR_PATTERN = /[!@#$%^&*]/;

const AddSystemCaregiver = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('editId');
  const isEditMode = Boolean(editId);

  const { currentSystemCaregiver } = useSelector(state => state.systemCaregiver);
  const excludeUserId = isEditMode ? (currentSystemCaregiver?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [practices, setPractices] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [showPracticeDropdown, setShowPracticeDropdown] = useState(false);
  const [practiceSearchTerm, setPracticeSearchTerm] = useState('');
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [selectedPracticeForModal, setSelectedPracticeForModal] = useState(null);
  const [practicePatients, setPracticePatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const practiceDropdownRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (practiceDropdownRef.current && !practiceDropdownRef.current.contains(event.target)) {
        setShowPracticeDropdown(false);
      }
    };

    if (showPracticeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPracticeDropdown]);

  useEffect(() => {
    if (!showPracticeDropdown) {
      setPracticeSearchTerm('');
    }
  }, [showPracticeDropdown]);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    middleInitial: '',
    lastName: '',
    username: '',
    password: '',
    confirmPassword: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    stateId: null,
    zipCode: '',
    homePhone: '',
    cellPhone: '',
    fax: '',
    email: '',
    twoWayAuth: 'Disabled',
    status: 'Active',
    timeZone: ''
  });

  const [initialState, setInitialState] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialState) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;

    const current = {
      formData: { ...formData, password: '', confirmPassword: '' },
      selectedPractices: selectedPractices.map(p => ({ practiceId: p.practiceId, patientIds: p.patientIds }))
    };

    return JSON.stringify(current) !== JSON.stringify(initialState);
  }, [formData, selectedPractices, isEditMode, initialState]);

  const [errors, setErrors] = useState({});

  const toSafeTrimmedString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  };

  // Fetch caregiver data if in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      dispatch(fetchSystemCaregiverById(editId));
    }
  }, [isEditMode, editId, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    updateBreadcrumbs([
      { label: 'System Caregivers', path: '/system-caregiver-dashboard' },
      { label: isEditMode ? 'Edit Caregiver' : 'Add Caregiver', path: null, active: true }
    ]);
  }, [isEditMode, updateBreadcrumbs]);

  // Populate form when caregiver data is loaded
  useEffect(() => {
    if (isEditMode && currentSystemCaregiver) {
      const stateId = currentSystemCaregiver.address?.state_id || currentSystemCaregiver.state_id;
      const state = US_STATES.find(s => s.id === stateId)?.name || '';

      const mappedData = {
        title: currentSystemCaregiver.title_id ? String(currentSystemCaregiver.title_id) : '',
        firstName: currentSystemCaregiver.first_name || '',
        middleInitial: currentSystemCaregiver.middle_name || '',
        lastName: currentSystemCaregiver.last_name || '',
        username: currentSystemCaregiver.username || '',
        password: '',
        confirmPassword: '',
        addressLine1: currentSystemCaregiver.address?.address_line_1 || currentSystemCaregiver.address_line_1 || '',
        addressLine2: currentSystemCaregiver.address?.address_line_2 || currentSystemCaregiver.address_line_2 || '',
        city: currentSystemCaregiver.address?.city || currentSystemCaregiver.city || '',
        state: state,
        stateId: stateId || null,
        zipCode: String(currentSystemCaregiver.address?.zip_code ?? currentSystemCaregiver.zip_code ?? ''),
        homePhone: formatPhoneNumber(currentSystemCaregiver.cell_phone || ''),
        cellPhone: formatPhoneNumber(currentSystemCaregiver.phone || ''),
        fax: formatPhoneNumber(currentSystemCaregiver.fax || ''),
        email: currentSystemCaregiver.email || '',
        twoWayAuth: (currentSystemCaregiver.is_two_way_auth === 1 || currentSystemCaregiver.is_two_way_auth === true) ? 'Enabled' : 'Disabled',
        status: currentSystemCaregiver.status ? (currentSystemCaregiver.status.charAt(0).toUpperCase() + currentSystemCaregiver.status.slice(1).toLowerCase()) : 'Active',
        timeZone: normalizeTimeZone(currentSystemCaregiver.time_zone || currentSystemCaregiver.timeZone || currentSystemCaregiver.timezone) || ''
      };

      setFormData(mappedData);
      setLoadedCredentials({
        username: mappedData.username || '',
        email: mappedData.email || '',
      });

      // Populate practices
      let practicesBatch = [];
      if (currentSystemCaregiver.practices && Array.isArray(currentSystemCaregiver.practices)) {
        practicesBatch = currentSystemCaregiver.practices
          .filter(isEvitalsMonitoringPractice)
          .map(p => ({
            practiceId: p.id,
            practiceName: p.name || p.practice_name || `Practice ${p.id}`,
            patientIds: p.patient_ids || []
          }));
        setSelectedPractices(practicesBatch);
      }

      setInitialState({
        formData: mappedData,
        selectedPractices: practicesBatch.map(p => ({ practiceId: p.practiceId, patientIds: p.patientIds }))
      });
    }
  }, [isEditMode, currentSystemCaregiver]);

  // Fetch practices on component mount
  useEffect(() => {
    const fetchPractices = async () => {
      try {
        setLoadingPractices(true);
        const response = await practiceService.getPractices({ limit: 1000, evitals_monitoring_only: 1 });

        let practicesList = [];
        if (response && response.data) {
          if (response.data.success && response.data.data) {
            if (response.data.data.practices && Array.isArray(response.data.data.practices)) {
              practicesList = response.data.data.practices;
            } else if (Array.isArray(response.data.data)) {
              practicesList = response.data.data;
            }
          } else if (Array.isArray(response.data)) {
            practicesList = response.data;
          }
        }

        practicesList = practicesList.filter(isEvitalsMonitoringPractice);

        setPractices(practicesList);
      } catch (error) {
        console.error('Error fetching practices:', error);
        toast.error('Failed to load practices');
      } finally {
        setLoadingPractices(false);
      }
    };

    fetchPractices();
  }, []);

  // Handle practice click - open patient selection modal only when practice has patients
  const handlePracticeClick = async (practice) => {
    setShowPracticeDropdown(false);
    setLoadingPatients(true);
    setPracticePatients([]);
    setSelectedPatientIds([]);

    try {
      const response = await practiceService.getPracticePatients(practice.id, { limit: 1000 });

      let patientsList = [];
      if (response && response.data) {
        if (response.data.success && response.data.data) {
          if (response.data.data.patients && Array.isArray(response.data.data.patients)) {
            patientsList = response.data.data.patients;
          } else if (Array.isArray(response.data.data)) {
            patientsList = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          patientsList = response.data;
        }
      }

      if (!patientsList.length) {
        // No patients — assign practice without opening the empty modal
        const practiceSelection = {
          practiceId: practice.id,
          practiceName: practice.practice_name || `Practice ${practice.id}`,
          patientIds: []
        };
        const existingIndex = selectedPractices.findIndex((sp) => sp.practiceId === practiceSelection.practiceId);
        if (existingIndex >= 0) {
          const updated = [...selectedPractices];
          updated[existingIndex] = practiceSelection;
          setSelectedPractices(updated);
        } else {
          setSelectedPractices([...selectedPractices, practiceSelection]);
        }
        setErrors((prev) => {
          if (!prev.practices) return prev;
          const next = { ...prev };
          delete next.practices;
          return next;
        });
        toast.info('No patients found for this practice. Practice assigned without patient selection.');
        return;
      }

      const existing = selectedPractices.find((sp) => sp.practiceId === practice.id);
      setSelectedPracticeForModal(practice);
      setPracticePatients(patientsList);
      setSelectedPatientIds(existing?.patientIds ? [...existing.patientIds] : []);
      setShowPatientModal(true);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error('Failed to load patients for this practice');
    } finally {
      setLoadingPatients(false);
    }
  };

  // Handle patient selection in modal
  const handlePatientToggle = (patientId) => {
    setSelectedPatientIds(prev =>
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  // Handle select all patients
  const handleSelectAllPatients = () => {
    if (selectedPatientIds.length === practicePatients.length) {
      setSelectedPatientIds([]);
    } else {
      setSelectedPatientIds(practicePatients.map(p => p.id));
    }
  };

  // Save practice with selected patients
  const handleSavePracticeSelection = () => {
    if (!selectedPracticeForModal) return;

    const practiceSelection = {
      practiceId: selectedPracticeForModal.id,
      practiceName: selectedPracticeForModal.practice_name || `Practice ${selectedPracticeForModal.id}`,
      patientIds: selectedPatientIds
    };

    const existingIndex = selectedPractices.findIndex(sp => sp.practiceId === practiceSelection.practiceId);
    if (existingIndex >= 0) {
      const updated = [...selectedPractices];
      updated[existingIndex] = practiceSelection;
      setSelectedPractices(updated);
    } else {
      setSelectedPractices([...selectedPractices, practiceSelection]);
    }

    setErrors((prev) => {
      if (!prev.practices) return prev;
      const next = { ...prev };
      delete next.practices;
      return next;
    });

    // Close modal and reset
    setShowPatientModal(false);
    setSelectedPracticeForModal(null);
    setSelectedPatientIds([]);
    setPracticePatients([]);
  };

  // Remove practice selection (at least one practice must remain)
  const handleRemovePractice = (practiceId) => {
    if (selectedPractices.length <= 1) {
      toast.warn('At least one practice must remain assigned.');
      return;
    }
    setSelectedPractices(selectedPractices.filter(sp => sp.practiceId !== practiceId));
    setErrors((prev) => {
      if (!prev.practices) return prev;
      const next = { ...prev };
      delete next.practices;
      return next;
    });
  };

  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = formatUSPhoneNumber;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // Phone Number Masking & Validation
    if (['homePhone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'homePhone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.2 State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      setErrors(newErrors);
      return;
    }

    // Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; 
      }
      if (value.length > 20) {
        return;
      }
    }

    // Password Validation (Real-time)
    if (name === 'password') {
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value.trim(), userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // Email Validation (Real-time)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      setFormData(prev => ({ ...prev, email: cleanedValue }));
      return;
    }

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password?.trim() && value.trim() !== formData.password.trim()) {
        newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const zipCodeValue = toSafeTrimmedString(formData.zipCode);

    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }
    if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';

    // Password Validation
    const trimmedPassword = formData.password?.trim() || '';
    const trimmedConfirmPassword = formData.confirmPassword?.trim() || '';
    const userData = { firstName: formData.firstName, lastName: formData.lastName };

    if (!isEditMode) {
      if (!trimmedPassword) {
        newErrors.password = 'Password is required';
      } else {
        const validation = validatePassword(trimmedPassword, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (trimmedPassword) {
      const validation = validatePassword(trimmedPassword, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    if (!isEditMode && !trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Confirm Password is required';
    }

    if (trimmedPassword && trimmedPassword !== trimmedConfirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state || formData.state === 'Select' || !formData.state?.trim()) newErrors.state = 'State is required';
    
    if (!zipCodeValue) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (zipCodeValue.length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }
    
    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    const homePhoneError = validateRequiredPhone(formData.homePhone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.homePhone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    if (!selectedPractices.length) {
      newErrors.practices = 'At least one practice is required';
    }

    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
    const emailOk = await ensureEmailAvailable(formData.email, setErrors);
    if (!usernameOk || !emailOk) {
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    if (selectedPractices.length === 0) {
      toast.error('Please select at least one practice');
      return;
    }

    try {
      setIsSubmitting(true);

      const trimmedPassword = formData.password?.trim() || '';

      const systemCaregiverData = {
        title_id: parseInt(formData.title) || null,
        first_name: formData.firstName,
        last_name: formData.lastName,
        middle_name: formData.middleInitial,
        email: formData.email,
        phone: (formData.cellPhone || '').replace(/\D/g, ''),
        cell_phone: (formData.homePhone || '').replace(/\D/g, ''),
        fax: formData.fax,
        address_line_1: formData.addressLine1,
        address_line_2: formData.addressLine2,
        city: formData.city,
        state_id: formData.stateId || 1,
        zip_code: formData.zipCode,
        username: formData.username,
        time_zone: formData.timeZone,
        ...(trimmedPassword ? { password: trimmedPassword } : {}),
        status: formData.status,
        is_two_way_auth: formData.twoWayAuth === 'Enabled' ? 1 : 0,
        practice_ids: selectedPractices.map(sp => sp.practiceId),
        practice_patient_assignments: selectedPractices.map(sp => ({
          practice_id: sp.practiceId,
          patient_ids: sp.patientIds || []
        }))
      };

      if (isEditMode) {
        await dispatch(updateSystemCaregiver({ systemCaregiverId: editId, systemCaregiverData })).unwrap();
        toast.success('System Caregiver updated successfully!');
      } else {
        await dispatch(createSystemCaregiver(systemCaregiverData)).unwrap();
        toast.success('System Caregiver created successfully!');
      }
      dispatch(invalidateSystemCaregiverListCache());
      navigate(-1, { state: { refreshSystemCaregiverList: true } });
    } catch (error) {
      console.error('Error saving system caregiver:', error);
      if (applyPasswordServerFieldError(setErrors, error)) {
        return;
      }
      const errorMessage = extractSubmissionErrorMessage(error, 'Failed to save system caregiver. Please try again.');
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  if (isEditMode && !currentSystemCaregiver) {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={9} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={6} columns={1} showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <>
      <AddFormModel
        title={isEditMode ? 'Edit System Caregiver' : 'Add System Caregiver'}
        currentPractice={null}
        getLogoUrl={null}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        leftSection={
          <div className="asc-form-single-column">
            <div className="pif-demo-section-label">Personal Information</div>

            <div className="row-name-double">
              <div className="ac-field w-title">
                <label htmlFor="title">Title</label>
                <select id="title" name="title" value={formData.title} onChange={handleInputChange}>
                  <option value="">- Select -</option>
                  {TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                {errors.title && <span className="error-text">{errors.title}</span>}
              </div>
              <div className="field">
                <label htmlFor="firstName">First Name <span className="required">*</span></label>
                <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                {errors.firstName && <span className="error-text">{errors.firstName}</span>}
              </div>
            </div>

            <div className="row-middle-last">
              <div className="field">
                <label htmlFor="middleInitial">Middle Name</label>
                <input type="text" id="middleInitial" name="middleInitial" value={formData.middleInitial} onChange={handleInputChange} placeholder="e.g., Marie" maxLength={20} />
              </div>
              <div className="field">
                <label htmlFor="lastName">Last Name <span className="required">*</span></label>
                <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                {errors.lastName && <span className="error-text">{errors.lastName}</span>}
              </div>
            </div>

            <div className="row-double row-triple">
              <div className="field">
                <label htmlFor="homePhone">
                  Home Phone
                  <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX-XXXX</Tooltip>}>
                    <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
                  </OverlayTrigger>
                </label>
                <PhoneNumberInput id="homePhone" name="homePhone" value={formData.homePhone} onChange={handleInputChange} />
                {errors.homePhone && <span className="error-text">{errors.homePhone}</span>}
              </div>
              <div className="field">
                <label htmlFor="cellPhone">
                  Cell Phone <span className="required">*</span>
                </label>
                <PhoneNumberInput id="cellPhone" name="cellPhone" value={formData.cellPhone} onChange={handleInputChange} required />
                {errors.cellPhone && <span className="error-text">{errors.cellPhone}</span>}
              </div>
              <div className="field">
                <label htmlFor="fax">
                  Fax
                  <OverlayTrigger placement="top" overlay={<Tooltip>Format: (XXX) XXX-XXXX</Tooltip>}>
                    <FontAwesomeIcon icon={faInfoCircle} className="field-info-icon" />
                  </OverlayTrigger>
                </label>
                <PhoneNumberInput id="fax" name="fax" value={formData.fax} onChange={handleInputChange} />
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

            <div className="row-double">
              <div className="field">
                <label htmlFor="username">Username <span className="required">*</span></label>
                <input type="text" id="username" name="username" value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} placeholder="e.g., john.doe" required maxLength={USERNAME_MAX_LENGTH} />
                {errors.username && <span className="error-text">{errors.username}</span>}
              </div>
              <div className="field">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onPaste={(e) => handleEmailPaste(e, setErrors)}
                  onKeyDown={(e) => { if (e.key === ' ') e.preventDefault(); }}
                  placeholder="e.g., caregiver@example.com"
                  required
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>
            </div>

            <div className="row-double">
              <div className="field">
                <label htmlFor="password">
                  Password {!isEditMode && <span className="required">*</span>}
                </label>
                <div className="password-wrapper ap-password-wrapper" style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    className={errors.password ? 'is-invalid' : ''}
                    value={formData.password}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"}
                    onFocus={() => setShowPasswordCriteria(true)}
                    onBlur={() => setShowPasswordCriteria(false)}
                    style={{ paddingRight: '30px' }}
                  />
                  <span className="eye-icon" onClick={togglePasswordVisibility}>
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </span>
                  {showPasswordCriteria && (
                    <div className="ap-password-criteria-block">
                      <div className={`ap-criteria-item ${formData.password.length >= 8 ? 'fulfilled' : ''}`}>
                        <FontAwesomeIcon icon={formData.password.length >= 8 ? faCheckCircle : faCircle} className="ap-criteria-icon" /> Minimum 8 characters
                      </div>
                      <div className={`ap-criteria-item ${/[A-Z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                        <FontAwesomeIcon icon={/[A-Z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Uppercase letter
                      </div>
                      <div className={`ap-criteria-item ${/[a-z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                        <FontAwesomeIcon icon={/[a-z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Lowercase letter
                      </div>
                      <div className={`ap-criteria-item ${/[0-9]/.test(formData.password) ? 'fulfilled' : ''}`}>
                        <FontAwesomeIcon icon={/[0-9]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Number
                      </div>
                      <div className={`ap-criteria-item ${PASSWORD_SPECIAL_CHAR_PATTERN.test(formData.password) ? 'fulfilled' : ''}`}>
                        <FontAwesomeIcon icon={PASSWORD_SPECIAL_CHAR_PATTERN.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Special character (!@#$%^&*)
                      </div>
                    </div>
                  )}
                </div>
                {errors.password && <span className="error-text">{errors.password}</span>}
              </div>
              <div className="field">
                <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="required">*</span>}</label>
                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    autoComplete="new-password"
                    placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"}
                  />
                  <span className="eye-icon" onClick={toggleConfirmPasswordVisibility}>
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </span>
                </div>
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

            <div className="field full-width">
              <label htmlFor="addressLine1">Address Line 1 <span className="required">*</span></label>
              <input type="text" id="addressLine1" name="addressLine1" value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
              {errors.addressLine1 && <span className="error-text">{errors.addressLine1}</span>}
            </div>

            <div className="field full-width">
              <label htmlFor="addressLine2">Address Line 2</label>
              <input type="text" id="addressLine2" name="addressLine2" value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
            </div>

            <div className="row-city-triple">
              <div className="field">
                <label htmlFor="city">City <span className="required">*</span></label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                {errors.city && <span className="error-text">{errors.city}</span>}
              </div>
              <div className="row-state-zip">
                <div className="field">
                  <label htmlFor="state">State <span className="required">*</span></label>
                  <select id="state" name="state" value={formData.state} onChange={handleInputChange} required>
                    <option value="">- Select -</option>
                    {US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                  </select>
                  {errors.state && <span className="error-text">{errors.state}</span>}
                </div>
                <div className="field">
                  <label htmlFor="zipCode">Zip Code <span className="required">*</span></label>
                  <input type="text" id="zipCode" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                  {errors.zipCode && <span className="error-text">{errors.zipCode}</span>}
                </div>
              </div>
            </div>

            <div className="field full-width">
              <label htmlFor="timeZone">Time Zone <span className="required">*</span></label>
              <select id="timeZone" name="timeZone" value={formData.timeZone} onChange={handleInputChange}>
                <option value="">- Select -</option>
                {TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
              </select>
              {errors.timeZone && <span className="error-text">{errors.timeZone}</span>}
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

            <div className="row-double">
              <div className="field">
                <label htmlFor="status">Status <span className="required">*</span></label>
                <select id="status" name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="twoWayAuth">Two Way Authentication <span className="required">*</span></label>
                <select id="twoWayAuth" name="twoWayAuth" value={formData.twoWayAuth} onChange={handleInputChange}>
                  <option value="Enabled">Enabled</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>
            </div>

            <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Practice Assignment</div>

            <div className="field full-width" style={{ position: 'relative' }} ref={practiceDropdownRef}>
              <label>Practice <span className="required">*</span></label>
              <div
                className="practice-select-trigger"
                onClick={() => setShowPracticeDropdown(true)}
              >
                {selectedPractices.length > 0 && (
                  selectedPractices.map(practice => (
                    <span key={practice.practiceId} className="practice-tag">
                      {practice.practiceName}
                      {selectedPractices.length > 1 && (
                      <button
                        type="button"
                        className="practice-tag-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePractice(practice.practiceId);
                        }}
                      >
                        ×
                      </button>
                      )}
                    </span>
                  ))
                )}
                <input
                  type="text"
                  className="practice-select-input"
                  placeholder="Click to select practices..."
                  value={practiceSearchTerm}
                  onFocus={() => setShowPracticeDropdown(true)}
                  onChange={(e) => {
                    setPracticeSearchTerm(e.target.value);
                    setShowPracticeDropdown(true);
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {showPracticeDropdown && (
                <div className="practice-dropdown">
                  {loadingPractices ? (
                    <div className="practice-dropdown-empty">Loading practices...</div>
                  ) : practices.length === 0 ? (
                    <div className="practice-dropdown-empty">No practices found</div>
                  ) : practices
                      .filter(p => !selectedPractices.find(sp => sp.practiceId === p.id))
                      .filter(practice =>
                        matchesWordStart(
                          practice.practice_name || `Practice ${practice.id}`,
                          practiceSearchTerm
                        )
                      ).length === 0 ? (
                    <div className="practice-dropdown-empty">No matching practices</div>
                    ) : (
                    practices
                      .filter(p => !selectedPractices.find(sp => sp.practiceId === p.id))
                      .filter(practice =>
                        matchesWordStart(
                          practice.practice_name || `Practice ${practice.id}`,
                          practiceSearchTerm
                        )
                      )
                      .map(practice => (
                        <div
                          key={practice.id}
                          className="practice-dropdown-item"
                          onClick={() => handlePracticeClick(practice)}
                        >
                          {practice.practice_name || `Practice ${practice.id}`}
                        </div>
                      ))
                  )}
                </div>
              )}
              {errors.practices && <span className="error-text">{errors.practices}</span>}
            </div>

          </div>
        }
        rightSection={null}
        footerActions={
          <>
            {isEditMode ? (
              <button
                type="button"
                className="btn-submit"
                onClick={() => navigate(`/event-logs/System%20Caregiver/${editId}`)}
                title="View Change History"
                style={{ width: 'auto', paddingLeft: '15px', paddingRight: '15px', backgroundColor: 'var(--ev-navy-dark)', marginRight: 'auto', height: '40px' }}
              >
                <FontAwesomeIcon icon={faList} className="me-2" /> Event Logs
              </button>
            ) : (
              <div style={{ marginRight: 'auto' }}></div>
            )}
            <FormActionButtons
              onCancel={handleCancel}
              disabled={isSubmitting || (isEditMode && !isChanged)}
              isSubmitting={isSubmitting}
              isEditMode={isEditMode}
            />
          </>
        }
      />

      {/* Patient Selection Modal */}
      {showPatientModal && selectedPracticeForModal && (
        <div
          className="patient-modal-overlay"
          onClick={() => {
            setShowPatientModal(false);
            setSelectedPracticeForModal(null);
            setSelectedPatientIds([]);
            setPracticePatients([]);
          }}
        >
          <div className="patient-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="patient-modal-header">
              <h5>
                {loadingPatients ? (
                  <span className="patient-modal-heading-skeleton patient-modal-skeleton-shimmer" />
                ) : (
                  <>Select Patients — {selectedPracticeForModal.practice_name || `Practice ${selectedPracticeForModal.id}`}</>
                )}
              </h5>
              <button
                type="button"
                className="patient-modal-close"
                onClick={() => {
                  setShowPatientModal(false);
                  setSelectedPracticeForModal(null);
                  setSelectedPatientIds([]);
                  setPracticePatients([]);
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div className="patient-modal-body">
              {loadingPatients ? (
                <div className="patient-table-skeleton-wrap">
                  <div className="patient-table-skeleton-header">
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer medium" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                    <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                  </div>
                  {Array.from({ length: 6 }).map((_, idx) => (
                    <div className="patient-table-skeleton-row" key={idx}>
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer medium" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer long" />
                      <span className="patient-modal-skeleton-line patient-modal-skeleton-shimmer short" />
                    </div>
                  ))}
                </div>
              ) : practicePatients.length === 0 ? (
                <div className="patient-modal-empty">No patients found for this practice</div>
              ) : (
                <table className="patient-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>#</th>
                      <th>Patient Name</th>
                      <th style={{ width: '70px' }}>Status</th>
                      <th>Caregiver</th>
                      <th className="patient-table-select-col">
                        <label className="select-all-label">
                          <input
                            type="checkbox"
                            checked={selectedPatientIds.length === practicePatients.length && practicePatients.length > 0}
                            onChange={handleSelectAllPatients}
                          />
                          <span>All</span>
                        </label>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {practicePatients.map((patient, index) => {
                      const patientName = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || patient.name || 'N/A';
                      const caregiverName =
                        patient.caregiver_name ||
                        patient.caregiver ||
                        (patient.caregiver_first_name && patient.caregiver_last_name
                          ? `${patient.caregiver_first_name} ${patient.caregiver_last_name}`
                          : 'N/A');
                      // Backend returns status as number: 2=Active, 3=Pending, 4=Locked
                      // or as string: 'Active', 'active', etc.
                      const rawStatus = patient.status;
                      let statusLabel = 'Inactive';
                      let isActive = false;
                      if (rawStatus === 2 || rawStatus === '2' || (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'active')) {
                        statusLabel = 'Active';
                        isActive = true;
                      } else if (rawStatus === 3 || rawStatus === '3' || (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'pending')) {
                        statusLabel = 'Pending';
                      } else if (rawStatus === 4 || rawStatus === '4' || (typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'locked')) {
                        statusLabel = 'Locked';
                      }
                      const isSelected = selectedPatientIds.includes(patient.id);

                      return (
                        <tr
                          key={patient.id}
                          className={isSelected ? 'selected' : ''}
                          onClick={() => handlePatientToggle(patient.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{index + 1}</td>
                          <td>{patientName}</td>
                          <td>
                            <span className={`patient-status-badge ${isActive ? 'active' : statusLabel === 'Pending' ? 'pending' : 'inactive'}`}>
                              {statusLabel}
                            </span>
                          </td>
                          <td>{caregiverName}</td>
                          <td>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handlePatientToggle(patient.id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div className="patient-modal-footer">
              <button
                type="button"
                className="btn-modal-close"
                onClick={() => {
                  setShowPatientModal(false);
                  setSelectedPracticeForModal(null);
                  setSelectedPatientIds([]);
                  setPracticePatients([]);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn-modal-save"
                onClick={handleSavePracticeSelection}
                disabled={practicePatients.length > 0 && selectedPatientIds.length === 0}
              >
                Save Selection
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Warning Modal */}
      <Modal 
        show={showValidationModal} 
        onHide={() => setShowValidationModal(false)} 
        centered 
        className="add-system-caregiver-validation-modal"
      >
        <Modal.Body className="add-system-caregiver-modal-body">
          <div className="add-system-caregiver-modal-icon-wrapper">
            <FontAwesomeIcon icon={faInfoCircle} className="add-system-caregiver-modal-icon" />
          </div>
          <h4 className="add-system-caregiver-modal-title">Required Fields Missing</h4>
          <button 
            type="button"
            className="add-system-caregiver-modal-btn" 
            onClick={() => setShowValidationModal(false)}
          >
            OK
          </button>
        </Modal.Body>
      </Modal>

    </>
  );
};

export default AddSystemCaregiver;